const FALLBACK_LABEL = 'その他'

export type SelectOption = { value: number; label: string }

const CAMPAIGN_TYPE_OPTIONS: SelectOption[] = [
  { value: 5, label: 'フォロー&リポスト' },
  { value: 6, label: 'フォロー&引用' },
  { value: 7, label: 'フォロー&リポスト・引用' },
  { value: 8, label: 'フォロー&ポスト・リプライ' },
  { value: 9, label: 'フォロー&メンション' },
  { value: 1, label: 'WEBインスタントウィン（フォロー&リポスト）' },
  { value: 2, label: 'WEBインスタントウィン（フォロー&引用）' },
  { value: 3, label: 'WEBインスタントウィン（フォロー&リポスト・引用）' },
  { value: 4, label: 'WEBインスタントウィン(フォロー&メンション)' },
  { value: 0, label: FALLBACK_LABEL }
]

const INCENTIVE_OPTIONS: SelectOption[] = [
  { value: 1, label: 'ギフト券（〜5,000円）' },
  { value: 2, label: 'ギフト券（5,001〜10,000円）' },
  { value: 3, label: 'ギフト券（10,001円〜）' },
  { value: 4, label: '旅行・体験' },
  { value: 5, label: 'ファングッズ・限定品' },
  { value: 6, label: '現金・ポイント' },
  { value: 7, label: '商品・サンプル' },
  { value: 0, label: FALLBACK_LABEL }
]

const toLabelMap = (options: SelectOption[]): Map<number, string> =>
  new Map(options.map(option => [option.value, option.label]))

const CAMPAIGN_TYPE_LABELS = toLabelMap(CAMPAIGN_TYPE_OPTIONS)
const INCENTIVE_LABELS = toLabelMap(INCENTIVE_OPTIONS)

export function formatCampaignType(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-'
  return CAMPAIGN_TYPE_LABELS.get(value) ?? FALLBACK_LABEL
}

export type CampaignTypeLabelParts = {
  type: string
  detail: string
}

/**
 * テーブルセル表示用に、キャンペーン形式ラベルを「種別」と「（詳細）」に分割する。
 */
export function splitCampaignTypeLabel(value: number | null | undefined): CampaignTypeLabelParts {
  const label = formatCampaignType(value)
  const matchIndex = label.search(/[（(]/)
  if (matchIndex === -1) {
    return { type: label, detail: '' }
  }
  return { type: label.slice(0, matchIndex), detail: label.slice(matchIndex) }
}

/**
 * インセンティブラベルを返す（未知値は「その他」）。
 */
export function formatIncentive(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-'
  return INCENTIVE_LABELS.get(value) ?? FALLBACK_LABEL
}

/**
 * campaignType が「その他（0）」以外かどうかを判定する（一覧・詳細ドロワーの形式タグの色分け用）。
 */
export function isAteluSupportedFormat(value: number | null | undefined): boolean {
  if (value === null || value === undefined) return false
  return value !== 0
}

/**
 * トレンド最高順位に応じた強調タグのクラス名を返す（1=金・2=銀・3=銅、4 位以下は空）。
 */
export function getTrendRankTagClass(rank: number): string {
  if (rank === 1) return 'trend-rank-tag--gold'
  if (rank === 2) return 'trend-rank-tag--silver'
  if (rank === 3) return 'trend-rank-tag--bronze'
  return ''
}

export const campaignTypeOptions: SelectOption[] = CAMPAIGN_TYPE_OPTIONS

export const incentiveOptions: SelectOption[] = INCENTIVE_OPTIONS

/**
 * 当選人数の絞り込み選択肢。
 * 値は winnerCountMin/Max（両端含む）へ変換して送信し、判別不可は winnerCountUndetectable として送る。
 */
export type WinnerCountRangeOption = {
  value: string
  label: string
  min?: number
  max?: number
  undetectable?: boolean
}

export const WINNER_COUNT_RANGE_OPTIONS: WinnerCountRangeOption[] = [
  { value: '1', label: '1', min: 1, max: 1 },
  { value: '2-5', label: '2〜5', min: 2, max: 5 },
  { value: '6-10', label: '6〜10', min: 6, max: 10 },
  { value: '11-30', label: '11〜30', min: 11, max: 30 },
  { value: '31-100', label: '31〜100', min: 31, max: 100 },
  { value: '101-1000', label: '101〜1000', min: 101, max: 1000 },
  { value: '1001-', label: '1001〜', min: 1001 },
  { value: 'undetectable', label: '判別不可', undetectable: true }
]

/**
 * 適用中の winnerCount 条件から、対応する選択肢の value を逆引きする（URL 復元・再表示用）。
 */
export function resolveWinnerCountRangeValue(filters: {
  winnerCountMin?: number
  winnerCountMax?: number
  winnerCountUndetectable?: boolean
}): string | undefined {
  if (filters.winnerCountUndetectable) return 'undetectable'

  return WINNER_COUNT_RANGE_OPTIONS.find(
    option =>
      option.undetectable !== true && option.min === filters.winnerCountMin && option.max === filters.winnerCountMax
  )?.value
}
